import { useEffect, useState } from "react";
import { Check, CircleAlert, Download, Link, Shuffle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import Spinner from "@/components/Spinner";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  selectConfig,
  selectSharedWall,
  selectWallLayout,
  shuffle,
} from "../store/slices/configurationSlice";
import { selectBrickSeries } from "../store/slices/bricksSlice";
import { exportTextureSet } from "../wall/textureSet";
import { withWallLink } from "../share/wallLink";

const COPIED_FEEDBACK_MS = 2000;

const saveBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const WallActions = () => {
  const dispatch = useAppDispatch();
  const layout = useAppSelector(selectWallLayout);
  const config = useAppSelector(selectConfig);
  const brickSeries = useAppSelector(selectBrickSeries);
  const sharedWall = useAppSelector(selectSharedWall);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Set when the link could not be copied, to show it for copying by hand.
  const [manualLink, setManualLink] = useState<string | null>(null);
  const empty = layout.bricks.length === 0;

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const share = async () => {
    const link = withWallLink(window.location.href, sharedWall);
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      // No clipboard access, e.g. in an iframe without the clipboard-write permission.
      setManualLink(link);
    }
  };

  const download = async () => {
    setError(null);
    setProgress(0);
    try {
      const { blob, fileName } = await exportTextureSet({
        layout,
        brickSelection: config.brickSelection,
        brickSeries,
        joint: config.joint,
        jointSize: config.jointSize,
        onProgress: setProgress,
      });
      saveBlob(blob, fileName);
      setProgress(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <div className="grid grid-cols-3 gap-2">
        <Button variant="outline" disabled={empty} onClick={() => dispatch(shuffle())}>
          <Shuffle />
          Shuffle
        </Button>
        <Button variant="outline" disabled={empty} onClick={share}>
          {copied ? <Check /> : <Link />}
          {copied ? "Gekopieerd" : "Delen"}
        </Button>
        <Button disabled={empty || progress !== null} onClick={download}>
          <Download />
          Download
        </Button>
      </div>
      <Dialog open={progress !== null} onOpenChange={(open) => !open && error && setProgress(null)}>
        <DialogContent
          showCloseButton={error != null}
          onEscapeKeyDown={(e) => !error && e.preventDefault()}
          onPointerDownOutside={(e) => !error && e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Download voorbereiden</DialogTitle>
            <DialogDescription>
              De textures van deze muur worden gemaakt en als zip-bestand gedownload.
            </DialogDescription>
          </DialogHeader>
          {error ? (
            <Alert variant="destructive">
              <CircleAlert />
              <AlertTitle>De textures konden niet gemaakt worden.</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : (
            <div className="grid gap-2">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <Spinner />
                  Bezig…
                </span>
                <span className="tabular-nums">{Math.round((progress ?? 0) * 100)}%</span>
              </div>
              <Progress value={(progress ?? 0) * 100} aria-label="Voortgang" />
            </div>
          )}
          {error && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setProgress(null)}>
                Sluiten
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={manualLink !== null} onOpenChange={(open) => !open && setManualLink(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Muur delen</DialogTitle>
            <DialogDescription>
              Kopieer deze link. Wie hem opent, ziet precies deze muur.
            </DialogDescription>
          </DialogHeader>
          <Input
            readOnly
            aria-label="Link naar deze muur"
            value={manualLink ?? ""}
            onFocus={(e) => e.currentTarget.select()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualLink(null)}>
              Sluiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
